"use client"

import { useCallback, useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { ArrowUp, Loader2, Sparkles, X } from 'lucide-react'
import { longDateText, useLang, useT, type Key } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { AI_CHAT_EVENT, useAiBrief } from './use-ai-brief'

/**
 * 🤖 The AI manager as a chat (2026-09-30 — the owner: „მე მეგონა, რომ ამას
 * ჩატ-ბოქსით გააკეთებდი სადმე კუთხეში, როგორც ვებ-საიტებს აქვთ ჩატები").
 *
 * The first message is yesterday's note (the one Telegram and the accounting
 * card show); then the owner asks, /api/ai-ask answers from the shop's own
 * numbers. The OWNER only, only where the AI manager is switched on.
 *
 * Where it opens from:
 *  - a desktop launcher in the bottom-right corner — not on the till (its
 *    cart and pay button live there) or the terminal;
 *  - on a phone the corner belongs to each page's „+" button, so the chat opens
 *    from the accounting card's „ჰკითხე" (`openAiChat`) as a bottom sheet.
 * The conversation lives as long as the app is open; it is not stored.
 */

type Msg = { id: number; role: 'ai' | 'me'; text: string; pending?: boolean; failed?: boolean; caption?: string }

const SUGGEST: Key[] = ['acc.ai.s1', 'acc.ai.s2', 'acc.ai.s3']
const SEEN_KEY = 'martepos.aiNoteSeen'

const readSeen = () => { try { return localStorage.getItem(SEEN_KEY) } catch { return null } }
const writeSeen = (day: string) => { try { localStorage.setItem(SEEN_KEY, day) } catch { /* private mode */ } }

export function AiChat() {
  const t = useT()
  const lang = useLang()
  const apiLang = lang === 'ru' ? 'ru' : 'ka'
  const brief = useAiBrief(apiLang)
  const pathname = usePathname() ?? ''
  const [open, setOpen] = useState(false)
  const [msgs, setMsgs] = useState<Msg[]>([])
  const [text, setText] = useState('')
  const [seen, setSeen] = useState<string | null>(null)
  const nextId = useRef(1)
  const listRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const busy = msgs.some((m) => m.pending)

  useEffect(() => { setSeen(readSeen()) }, [])

  // The first message: yesterday's note, or what the owner may ask.
  useEffect(() => {
    if (!brief?.enabled) return
    setMsgs((list) => {
      if (list.length) return list
      if (brief.note && brief.day) {
        const [y, m, d] = brief.day.split('-').map(Number)
        return [{ id: nextId.current++, role: 'ai', text: brief.note, caption: t('acc.ai.noteOf', { day: longDateText(new Date(y, m - 1, d), { weekday: true }) }) }]
      }
      return [{ id: nextId.current++, role: 'ai', text: t('acc.ai.hello') }]
    })
  }, [brief, t])

  const show = useCallback(() => {
    setOpen(true)
    if (brief?.day) { writeSeen(brief.day); setSeen(brief.day) }
  }, [brief])

  // „ჰკითხე" on the accounting card
  useEffect(() => {
    const onOpen = () => show()
    window.addEventListener(AI_CHAT_EVENT, onOpen)
    return () => window.removeEventListener(AI_CHAT_EVENT, onOpen)
  }, [show])

  // Esc closes; the input takes the focus on a desktop (a phone would throw its keyboard up)
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    if (window.matchMedia('(min-width: 768px)').matches) inputRef.current?.focus()
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  // the newest message in view
  useEffect(() => {
    const el = listRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [msgs, open])

  const ask = async (question: string) => {
    const q = question.trim()
    if (q.length < 2 || busy) return
    setText('')
    const meId = nextId.current++
    const pendingId = nextId.current++
    setMsgs((list) => [...list, { id: meId, role: 'me', text: q }, { id: pendingId, role: 'ai', text: '', pending: true }])
    let answer: string
    let failed = false
    try {
      const r = await fetch('/api/ai-ask', {
        method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ question: q, lang: apiLang }),
      })
      const j = (await r.json().catch(() => ({}))) as { ok?: boolean; answer?: string }
      answer = j.answer || t('acc.ai.failed')
      failed = !j.ok
    } catch {
      answer = t('acc.ai.failed')
      failed = true
    }
    setMsgs((list) => list.map((m) => (m.id === pendingId ? { ...m, text: answer, failed, pending: false } : m)))
  }

  if (!brief?.enabled) return null
  const onTill = pathname.startsWith('/pos') || pathname.startsWith('/terminal')
  const unread = !!brief.note && !!brief.day && seen !== brief.day
  const asked = msgs.some((m) => m.role === 'me')

  return (
    <>
      {/* desktop launcher */}
      {!onTill && (
        <button
          type="button"
          onClick={() => (open ? setOpen(false) : show())}
          aria-label={open ? t('acc.ai.close') : t('acc.ai.ask')}
          aria-expanded={open}
          className="hidden md:flex fixed right-6 bottom-6 z-40 size-14 rounded-full bg-primary text-primary-foreground items-center justify-center shadow-[0_14px_30px_-12px_var(--color-primary)] transition-transform duration-150 active:scale-[0.97]"
        >
          {open ? <X className="size-5.5" /> : <Sparkles className="size-5.5" />}
          {!open && unread && <span className="absolute top-1 right-1 size-3 rounded-full bg-amber-400 ring-2 ring-background" aria-hidden="true" />}
        </button>
      )}

      {open && (
        <>
          {/* a phone's sheet dims the page behind it; a desktop panel does not */}
          <div className="md:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setOpen(false)} aria-hidden="true" />
          <section
            role="dialog"
            aria-label={t('acc.ai.title')}
            className={cn(
              'fixed z-50 flex flex-col bg-card border border-border shadow-2xl animate-fade-up overflow-hidden',
              'inset-x-0 bottom-[var(--kb-inset)] h-[min(85dvh,var(--vv-avail))] rounded-t-3xl',
              'md:inset-x-auto md:left-auto md:right-6 md:bottom-24 md:w-[400px] md:h-[min(620px,calc(100dvh-8rem))] md:rounded-2xl',
            )}
          >
            <header className="flex items-center gap-3 px-4 py-3 border-b border-border/70">
              <div className="size-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0" aria-hidden="true">
                <Sparkles className="size-4.5" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-bold text-foreground leading-tight">{t('acc.ai.title')}</h2>
                <p className="text-[12px] text-muted-foreground leading-snug truncate">{t('acc.ai.sub')}</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} aria-label={t('acc.ai.close')} className="size-9 rounded-xl flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                <X className="size-4.5" />
              </button>
            </header>

            <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3">
              {msgs.map((m) => (
                <div key={m.id} className={cn('flex flex-col gap-1 max-w-[88%]', m.role === 'me' ? 'self-end items-end' : 'self-start')}>
                  {m.caption && <p className="text-[12px] text-muted-foreground px-1">{m.caption}</p>}
                  <div
                    className={cn(
                      'rounded-2xl px-3.5 py-2.5 text-[14.5px] leading-relaxed whitespace-pre-line',
                      m.role === 'me' ? 'rounded-br-md bg-primary text-primary-foreground' : 'rounded-bl-md bg-muted',
                      m.role === 'ai' && (m.failed || m.pending ? 'text-muted-foreground' : 'text-foreground'),
                    )}
                  >
                    {m.pending
                      ? <span className="inline-flex items-center gap-2"><Loader2 className="size-3.5 animate-spin" aria-hidden="true" />{t('acc.ai.thinking')}</span>
                      : m.text}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-border/70 px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              {!asked && (
                <div className="flex flex-wrap gap-1.5 mb-2.5">
                  {SUGGEST.map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => void ask(t(k))}
                      className="rounded-full border border-border px-3 py-1.5 text-[12.5px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    >
                      {t(k)}
                    </button>
                  ))}
                </div>
              )}
              <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); void ask(text) }}>
                <input
                  ref={inputRef}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  maxLength={400}
                  placeholder={t('acc.ai.placeholder')}
                  aria-label={t('acc.ai.ask')}
                  className="h-11 min-w-0 flex-1 rounded-xl border border-border bg-background px-3.5 text-[15px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                <button
                  type="submit"
                  disabled={busy || text.trim().length < 2}
                  aria-label={t('acc.ai.ask')}
                  className="size-11 shrink-0 rounded-xl bg-primary text-primary-foreground flex items-center justify-center transition-transform duration-150 active:scale-[0.97] disabled:opacity-40"
                >
                  <ArrowUp className="size-5" />
                </button>
              </form>
            </div>
          </section>
        </>
      )}
    </>
  )
}
